// Pure queue logic for AchievementContext, kept apart so the "shown twice"
// cases are unit-tested without rendering the provider.

export interface QueuedBadge {
  id: string;
}

export interface QueueState<T extends QueuedBadge> {
  queue: T[];
  /** Position of the badge currently on screen; earlier ones are already viewed. */
  index: number;
}

/**
 * Badges from a server response that have not been shown or acknowledged in
 * this session, each at most once even if the server lists one twice.
 */
export function takeFresh<T extends QueuedBadge>(unseen: T[], alreadyShown: ReadonlySet<string>): T[] {
  const seenHere = new Set<string>();
  const fresh: T[] = [];
  for (const badge of unseen) {
    if (alreadyShown.has(badge.id) || seenHere.has(badge.id)) continue;
    seenHere.add(badge.id);
    fresh.push(badge);
  }
  return fresh;
}

/**
 * A screen has just shown these badges itself (quiz results, the badge page),
 * so the pop-up must not show them again. Removes them from the queue and
 * keeps the on-screen position pointing at the same badge, or at the next one
 * when the current badge was removed.
 */
export function acknowledgeInQueue<T extends QueuedBadge>(state: QueueState<T>, ids: readonly string[]): QueueState<T> {
  if (ids.length === 0) return state;
  const drop = new Set(ids);
  if (!state.queue.some((badge) => drop.has(badge.id))) return state;
  const queue = state.queue.filter((badge) => !drop.has(badge.id));
  if (queue.length === 0) return { queue, index: 0 };
  const keptBefore = state.queue.slice(0, state.index).filter((badge) => !drop.has(badge.id)).length;
  return { queue, index: Math.min(keptBefore, queue.length - 1) };
}
